import { ArrowRightTwoIcon, CloseIcon, RightArrowIcon } from '@sketchnote/icons';
import { SVGComponentProps } from '@sketchnote/icons';
import classNames from 'classnames';
import Link from 'next/link';
import * as React from 'react';
import { useForm, useFormState } from 'react-hook-form';

import { isMacintosh } from '../../shared/utils';
import { Button } from '../button';
import { FileItem, FileUpload } from '../file-upload';
import { Input } from '../text-input';
import { Textarea } from '../textarea';
import { TileButton } from '../tile-button/tile-button.component';
import { VideoCard } from '../video-card';

const { useEffect } = React;

export interface IconBorderTextProps
  extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  text: string;
  icon?: JSX.Element;
  leftIcon?: JSX.Element;
  leftIconClass?: string;
}

export const IconBorderText = ({
  text,
  icon,
  className,
  leftIcon,
  leftIconClass,
  ...otherProps
}: IconBorderTextProps): JSX.Element => (
  <div
    className={classNames({
      'flex py-20 justify-between text-gray-600 text-sm font-medium items-center border-b border-gray-200 hover:text-gray-800 cursor-pointer':
        true,
      [className || '']: !!className,
    })}
    role="button"
    {...otherProps}
  >
    <div>
      <span className={`pr-4 inline-block ${leftIconClass}`}>{leftIcon}</span>
      <span className="pl-4" dangerouslySetInnerHTML={{ __html: text }} />
    </div>
    {icon}
  </div>
);

export interface IconButtonProps
  extends React.DetailedHTMLProps<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  icon: ({ ...otherProps }: SVGComponentProps) => JSX.Element;
  label: string;
}

export const IconButton = ({ icon: Icon, label, ...otherProps }: IconButtonProps) => (
  <button
    className="flex items-center text-gray-600 text-sm font-medium hover:text-gray-800 mb-24 cursor-pointer"
    {...otherProps}
  >
    <Icon wrapperClass="mr-8" />
    <span>{label}</span>
  </button>
);

export interface FeedbackProps {
  translations: {
    send_feedback_title: string;
    send_feedback_desc: string;
    send_feedback_label: string;
    send_feedback_placeholder: string;
    attachments_title: string;
    attachments_desc: string;
    attachments_label: string;
    attachments_drop_label: string;
    feedback_question: string;
    feedback_action_btn: string;
  };
}

export const Feedback = ({ translations }: FeedbackProps): JSX.Element => {
  const { register, handleSubmit, control, setError, setValue, getValues, watch } = useForm();
  const { errors } = useFormState({ control });
  const feedback = watch('feedback');
  const attachments = watch('attachments');
  const onSubmit = (values) => {
    console.log(values);
  };
  useEffect(() => {
    register('attachments');
    register('feedback');
  }, [register]);
  const handleFileUpload = (file) => {
    file.push(...(attachments || []));
    setValue('attachments', file);
  };
  const handleFileRemove = (file) => {
    setValue(
      'attachments',
      attachments.filter((attachment) => attachment !== file)
    );
  };
  const handleFeedback = (value) => {
    setValue('feedback', value);
  };
  return (
    <form className="mb-40" onSubmit={handleSubmit(onSubmit)}>
      <h5 className="text-lg mb-8">{translations.send_feedback_title}</h5>
      <p className="text-sm mb-32 text-gray-999">{translations.send_feedback_desc}</p>
      <div className="mb-40">
        <Textarea
          placeholder={translations.send_feedback_placeholder}
          wrapperClassName="bg-gray-100 px-12 py-13"
          inputProps={{
            rows: 5,
            ...register('description', {
              required: true,
            }),
          }}
          label={translations.send_feedback_label}
          required={true}
          status={errors.description ? 'danger' : undefined}
          caption={errors?.description?.message}
        />
      </div>
      <div className="mb-40">
        <h5 className="text-lg mb-16">{translations.attachments_title}</h5>
        <p className="text-sm mb-16 text-gray-999">{translations.attachments_desc}</p>
        <FileUpload translations={translations} onDrop={handleFileUpload} className="mb-8" />
        {attachments?.map((attachment, index: number) => (
          <FileItem
            fileName={attachment.name}
            fileSize={attachment.size}
            key={`${attachment.size}-${index}`}
            onRemove={handleFileRemove.bind(this, attachment)}
            className="mb-8"
          />
        ))}
      </div>
      <div className="mb-40">
        <h5 className="text-base text-gray-999 mb-16">{translations.feedback_question}</h5>
        {[
          { label: 'Very', value: 'very' },
          { label: 'Somewhat', value: 'somewhat' },
          { label: 'Not at all', value: 'not-at-all' },
        ].map((item) => (
          <TileButton
            selected={item.value === feedback}
            className="mb-8"
            key={`${item.value}`}
            onClick={handleFeedback.bind(this, item.value)}
          >
            {item.label}
          </TileButton>
        ))}
      </div>
      <div className="flex justify-end">
        <Button status="primary" className="px-24 py-13" type="submit">
          {translations.feedback_action_btn}
        </Button>
      </div>
    </form>
  );
};

export interface BugReportProps {
  translations: {
    bug_report_title: string;
    bug_report_desc: string;
    bug_issue_label: string;
    bug_explain_title: string;
    bug_explain_desc: string;
    bug_explain_label: string;
    bug_attachements_title: string;
    bug_attachements_desc: string;
    attachments_label: string;
    attachments_drop_label: string;
    bug_action_btn: string;
  };
}

export const BugReport = ({ translations }: BugReportProps): JSX.Element => {
  const { register, handleSubmit, control, setError, setValue, getValues, watch } = useForm();
  const attachments = watch('attachments');
  const { errors } = useFormState({ control });

  useEffect(() => {
    register('attachments');
  }, [register]);
  const onSubmit = (values) => {
    console.log(values);
  };
  const handleFileUpload = (file) => {
    file.push(...(attachments || []));
    setValue('attachments', file);
  };
  const handleFileRemove = (file) => {
    setValue(
      'attachments',
      attachments.filter((attachment) => attachment !== file)
    );
  };
  return (
    <form className="mb-40" onSubmit={handleSubmit(onSubmit)}>
      <h5 className="text-lg mb-8">{translations.bug_report_title}</h5>
      <p className="text-sm mb-32 text-gray-999">{translations.bug_report_desc}</p>
      <Input
        label={translations.bug_issue_label}
        required={true}
        inputProps={{
          ...register('issue', {
            required: true,
          }),
        }}
        wrapperClassName="bg-gray-100 px-12"
        wrapperStyle={{ paddingTop: 7, paddingBottom: 7 }}
        textSecClassName="mb-40"
        status={errors.description ? 'danger' : undefined}
        caption={errors?.description?.message}
      />
      <h5 className="text-lg mb-8">{translations.bug_explain_title}</h5>
      <p className="text-sm mb-32 text-gray-999">{translations.bug_explain_desc}</p>
      <Textarea
        wrapperClassName="bg-gray-100 px-12 py-13"
        textSecClassName="mb-40"
        inputProps={{
          rows: 5,
          ...register('description', {
            required: true,
          }),
        }}
        label={translations.bug_explain_label}
        required={true}
        status={errors.description ? 'danger' : undefined}
        caption={errors?.description?.message}
      />
      <h5 className="text-lg mb-8">{translations.bug_attachements_title}</h5>
      <p className="text-sm mb-16 text-gray-999">{translations.bug_attachements_desc}</p>
      <FileUpload className="mb-8" translations={translations} onDrop={handleFileUpload} />
      {attachments?.map((attachment, index: number) => (
        <FileItem
          fileName={attachment.name}
          fileSize={attachment.size}
          key={`${attachment.size}-${index}`}
          onRemove={handleFileRemove.bind(this, attachment)}
          className="mb-8"
        />
      ))}
      <div className="flex justify-end mt-40">
        <Button status="primary" className="px-24 py-13" type="submit">
          {translations.bug_action_btn}
        </Button>
      </div>
    </form>
  );
};

export interface DemoProps {
  title: string;
  description: string;
  icon: ({ ...otherProps }: SVGComponentProps) => JSX.Element;
  thumbnailUrl: string;
  videoId: string;
  onClose?: () => void;
  translations: {
    video_thumbnail_label: string;
    more_action_btn_label: string;
  };
}

export const Demo = ({
  title,
  icon: Icon,
  description,
  thumbnailUrl,
  videoId,
  onClose,
  translations,
}: DemoProps): JSX.Element => {
  return (
    <div>
      <h5 className="text-base flex items-center mb-22">
        <Icon wrapperClass="mr-16" />
        {title}
      </h5>
      {/* <p className="mb-32 text-gray-999 bg-green-500">{description}</p> */}
      <VideoCard
        translations={translations}
        thumbnailUrl={thumbnailUrl}
        videoId={videoId}
        className="mb-34"
        onClose={onClose}
      />
      <Link href={'/help'}>
        <a target="_blank">
          <Button status="ghost" iconRight={<RightArrowIcon />} style={{ paddingLeft: 0 }}>
            More info
          </Button>
        </a>
      </Link>
    </div>
  );
};

export interface ShortcutsProps {
  shortcutSections: { title: string; bindings: { label: string; keys: string[] }[] }[];
}

export const Shortcuts = ({ shortcutSections }: ShortcutsProps): JSX.Element => {
  const changeKey = (key: string) => {
    if (key === '⌘' || key === '⌥') {
      return isMacintosh() ? key : key === '⌘' ? 'Ctrl' : key === '⌥' ? 'Alt' : key;
    }
    return key;
  };
  return (
    <div>
      <h5 className="text-lg mb-10">Shortcuts</h5>
      <p className="text-sm mb-48 mt-16">
        <b>Note</b>: You&apos;ll see <span className="text-blue-500">cmd/ctrl</span> and &nbsp;
        <span className="text-blue-500">option/alt</span> used a lot below. This indicates command
        and option on Mac and control and alt on Windows and Linux.
      </p>
      {shortcutSections.map((shortcuts) => (
        <div key={shortcuts.title} className="pt-20 pb-30">
          <p className="text-gray-600 text-lg mb-22">{shortcuts.title}</p>
          {shortcuts.bindings.map((binding, index) => (
            <div
              className="flex p-10 items-center border-b border-gray-200 justify-between"
              key={`${binding.label}-${index}`}
            >
              <span className="font-medium text-black text-sm">{binding.label}</span>
              <div className="flex">
                {binding.keys.map((key, keyIndex) => (
                  <span
                    className="bg-gray-100 text-xs rounded mr-2 px-8 py-2 flex justify-center items-center font-medium text-gray-999"
                    key={`${key}-${keyIndex}`}
                  >
                    {changeKey(key)}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export const Header = ({
  title,
  onCloseClick,
  onBackClick,
}: {
  title?: string;
  onCloseClick?: () => void;
  onBackClick?: () => void;
}): JSX.Element => (
  <div className="flex justify-between items-center py-16 mb-24">
    <h6 className="text-base flex items-center">
      {onBackClick ? (
        <button onClick={onBackClick}>
          <ArrowRightTwoIcon wrapperClass="text-gray-500 transform rotate-180 mr-16" />
          {title}
        </button>
      ) : (
        title
      )}
    </h6>
    <button onClick={onCloseClick}>
      <CloseIcon className="text-gray-500 text-xs" />
    </button>
  </div>
);
